import {
	Box,
	Button,
	Card,
	Divider,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';

import {
	useDeleteSubFaqMutation,
	useSubFaqDetailQuery,
} from '@/api/information/subFaqApiSlice';
import SubFaqInfoForm from './components/SubFaqInfoForm';

export default function SubFaqDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteSubFaq] = useDeleteSubFaqMutation();
	const { faqId, questionId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');

	const { data: details } = useSubFaqDetailQuery(questionId || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('subFaq.deleteQuestion')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('subFaq.isDeleteQuestion')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteSubFaq(questionId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteQuestionSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate(`/information-manage/faq-list/sub-faq-list/${faqId}`, {
							replace: true,
						});
					}
				}}
			/>
		));
	};
	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.questionDetail')}
					</Typography>
					<Button
						endIcon={<DeleteOutlined />}
						onClick={() => handleDelete()}
						sx={{ marginRight: '24px' }}
						variant='contained'
					>
						{t('button.delete')}
					</Button>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('emo.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('emo.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('emo.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>{t('emo.content')}</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('emo.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('emo.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('subFaq.question')}
													value={
														currentTab == 'zh'
															? details?.data.questionZh
															: currentTab == 'en'
															? details?.data.questionEn
															: '-'
													}
												/>
												<AppFieldView
													label={t('subFaq.answer')}
													valueElement={
														<pre
															style={{
																background: 'none',
																color: 'rgba(0, 0, 0, 0.87)',
																overflowWrap: 'break-word',
																wordBreak: 'break-word',
																whiteSpace: 'normal',
																fontSize: '1rem',
																padding: 0,
																lineHeight: '1.5',
															}}
														>
															{currentTab == 'zh'
																? details?.data.answerZh
																: currentTab == 'en'
																? details?.data.answerEn
																: '-'}
														</pre>
													}
												/>
											</Grid>
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>{t('emo.content')}</Typography>
										</Box>
										<SubFaqInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
			</Box>
		</>
	);
}
